import { useInfiniteQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import Button from '@/components/Button';
import { Controls, ControlsButton } from '@/components/Controls';
import InfiniteLoader from '@/components/InfiniteLoader';
import { PageHeading, SrOnly, TeamHeading } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import SearchBar from '@/components/SearchBar';
import { SortableTh, Table, TableContent, Th } from '@/components/Table';
import useDebounce from '@/hooks/useDebounce';
import useSortState from '@/hooks/useSortState';
import { MLProject, PaginatedResponse } from '@/types/resources';
import { api } from '@/utils/api';
import { MLProjectStatus } from '@/utils/enums';

import Form from './components/Form';
import TableRow from './components/TableRow';

const HeadingRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const SectionHeader = styled.div<{ $pushRight?: boolean }>`
  display: flex;
  align-items: flex-end;
  margin: 0 0 12px;
  ${({ $pushRight }) =>
    $pushRight &&
    css`
      justify-content: flex-end;
    `}
`;

const ViewControls = styled(Controls)`
  margin-right: auto;
`;

const StyledTable = styled(Table)`
  td,
  th {
    vertical-align: top;
    &:first-child {
      padding-left: 24px;
    }
    &:last-child {
      padding-right: 24px;
    }
  }
`;

interface Props {
  status: MLProjectStatus;
}

const MLProjects: FC<Props> = ({ status }) => {
  const [showCreationForm, setShowCreationForm] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const { sort, setSort, sortQueryParamObj } = useSortState();

  const navigate = useNavigate();

  const fetchPosts = ({ pageParam = '/v1/distribution/ml-projects' }) => {
    return api.get<never, PaginatedResponse<MLProject>>(pageParam, {
      params: {
        ...sortQueryParamObj,
        'filter[status]': status,
        ...(!!debouncedSearch && {
          'filter[search]': debouncedSearch,
        }),
      },
    });
  };

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    [
      'ml_projects',
      {
        status,
        sort,
        ...(!!debouncedSearch && {
          search: debouncedSearch,
        }),
      },
    ],
    fetchPosts,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
      staleTime: 90 * 1000,
    },
  );

  const projects = useMemo(
    () =>
      data?.pages?.reduce<MLProject[]>((acc, curr) => {
        return [...acc, ...curr.data];
      }, []),
    [data],
  );

  return (
    <>
      <TeamHeading />
      <HeadingRow>
        <PageHeading>Multi-language Projects</PageHeading>
        <Button onClick={() => setShowCreationForm(true)}>
          Create project
        </Button>
      </HeadingRow>
      <SectionHeader>
        <ViewControls>
          <ControlsButton
            active={status === MLProjectStatus.PENDING}
            onClick={() => navigate(`../pending`, { relative: 'path' })}
          >
            Pending
          </ControlsButton>
          <ControlsButton
            active={status === MLProjectStatus.DONE}
            onClick={() => navigate(`../done`, { relative: 'path' })}
          >
            Done
          </ControlsButton>
          <ControlsButton
            active={status === MLProjectStatus.DRAFT}
            onClick={() => navigate(`../draft`, { relative: 'path' })}
          >
            Draft
          </ControlsButton>
        </ViewControls>
      </SectionHeader>
      <SearchBar
        value={search}
        setValue={setSearch}
        placeholder="Search by title"
      />
      <ScrollableTable>
        <StyledTable $stickyActions>
          <thead>
            <tr>
              <Th>Thumb & Title</Th>
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="createdAt"
                title="Date Created"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="updatedAt"
                title="Date Modified"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="translationsCount"
                title="Total Languages"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="draftReleasesCount"
                title="Ready to Distribute"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="releasesCount"
                title="Distributed"
              />
              <Th $actionsCol>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!projects?.length}
            >
              {projects?.map((project) => (
                <TableRow key={project.id} project={project} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
      <AnimatePresence>
        {showCreationForm && (
          <Form onClose={() => setShowCreationForm(false)} />
        )}
      </AnimatePresence>
    </>
  );
};

export default MLProjects;
